<template>
    <div class="kb-search">
        <div class="panel h1-panel">
            <div class="panel-heading">
                <h1><router-link to="/kb">Knowledge Base</router-link></h1>
            </div>
            <div class="panel-body">
                <h2>Search</h2>
            </div>
        </div>

        <section class="panel search-panel">
            <div class="panel-body">
                <h2>Search</h2>
                <form @submit.prevent="search()">
                    <div class="form-group">
                        <input
                            class="form-control"
                            type="text"
                            placeholder="Search for a topic (lighting, texturing, ...)"
                            v-model="query">
                        <button type="submit" class="clear-button">
                            <i class="fa fa-search"></i>
                        </button>
                    </div>

                    <button
                        class="link-button"
                        type="button"
                        @click="toggleFilters()">
                        <span v-if="!filtersShowing">Show Filters</span>
                        <span v-else>Hide Filters</span>
                    </button>
                </form>
            </div>
        </section>

        <section
            class="panel filter-panel"
            v-if="filtersShowing">
            <div class="panel-body">
                <h2>Filters</h2>

                <div
                    class="loading-msg"
                    v-if="isLoading">
                    Loading...
                </div>

                <form
                    @submit.prevent="search()"
                    v-else>
                    <div class="form-group">
                        <label>Category</label>
                        <multiselect
                            :options="categoryOptionsAsLabels"
                            v-model="categories">
                        </multiselect>
                    </div>

                    <div class="form-group">
                        <label>Term Type</label>
                        <multiselect
                            class="term-type-multiselect"
                            :options="termTypeOptions"
                            v-model="termTypes">
                        </multiselect>
                    </div>

                    <div class="form-group">
                        <label>Path</label>
                        <multiselect
                            :options="pathOptionsAsNames"
                            v-model="paths">
                        </multiselect>
                    </div>

                    <div class="form-group">
                        <label>Software</label>
                        <software-select
                            :options="softwareOptions"
                            v-model="software">
                        </software-select>
                    </div>

                    <div class="form-group">
                        <label>Tags</label>
                        <tag-select
                            :search="searchTags"
                            v-model="tags">
                        </tag-select>
                    </div>
                </form>
            </div>
        </section>

        <section class="panel results-panel">
            <div class="panel-body">
                <div
                    class="loading-msg"
                    v-if="isLoading">
                    Loading...
                </div>

                <div
                    class="loading-msg"
                    v-else-if="isSearching">
                    Searching...
                </div>

                <div v-else>
                    <div v-if="results.length > 0">
                        <h2>{{ formatSearchArticlesTitle(results) }}</h2>
                        <ol>
                            <li v-for="article in results">
                                <kb-block-link
                                    class="article-block"
                                    :to="getArticleLocation(article)">
                                    <div class="article-overview">
                                        <img
                                            :src="getCoverImageUrl(article)"
                                            v-if="getCoverImageUrl(article)">
                                        <img
                                            src="../assets/img/ttto-thumbnail.jpg"
                                            v-else>
                                        <h3>{{ article.title }}</h3>
                                        <p
                                            v-if="article.overviewHighlights"
                                            v-html="article.overviewHighlights"></p>
                                        <p
                                            v-else
                                            v-html="article.overview"></p>
                                    </div>

                                    <div
                                        class="article-search-info"
                                        v-show="doesArticleHaveAnySearchInfo(article)">
                                        <ul
                                            class="article-quotes"
                                            v-show="doesArticleHaveSearchHighlights(article)">
                                            <li v-for="hl in article.search.highlights">
                                                <blockquote v-html="hl"></blockquote>
                                            </li>
                                        </ul>

                                        <div
                                            class="article-details"
                                            v-show="doesArticleHaveSearchDetails(article)">
                                            <div
                                                class="info-group"
                                                v-if="article.search.software.length > 0">
                                                <div class="info-group-label">Software</div>
                                                <software-list :value="article.search.software">
                                                </software-list>
                                            </div>
                                            <div
                                                class="info-group"
                                                v-if="article.search.tags.length > 0">
                                                <div class="info-group-label">Tags</div>
                                                <tag-list :value="article.search.tags">
                                                </tag-list>
                                            </div>
                                            <div
                                                class="info-group"
                                                v-if="article.search.categories.length > 0">
                                                <div class="info-group-label">Categories</div>
                                                <option-list :value="article.search.categories">
                                                </option-list>
                                            </div>
                                            <div
                                                class="info-group"
                                                v-if="article.search.termTypes.length > 0">
                                                <div class="info-group-label">Term Types</div>
                                                <option-list :value="article.search.termTypes">
                                                </option-list>
                                            </div>
                                            <div
                                                class="info-group"
                                                v-if="article.search.paths.length > 0">
                                                <div class="info-group-label">Paths</div>
                                                <option-list :value="article.search.paths">
                                                </option-list>
                                            </div>
                                        </div>
                                    </div>
                                </kb-block-link>
                            </li>
                        </ol>
                    </div>

                    <div class="no-results" v-else>
                        <h2>{{ formatSearchArticlesTitle(results) }}</h2>
                    </div>
                </div>
            </div>
        </section>

        <kb-feedback></kb-feedback>
    </div>
</template>

<script>
    import toastr from 'toastr';

    import Multiselect from './reusable/multiselect';
    import OptionList from './reusable/option-list';
    import SoftwareList from './reusable/software-list';
    import SoftwareSelect from './reusable/software-select';
    import TagList from './reusable/tag-list';
    import TagSelect from './reusable/tag-select';
    import KbBlockLink from './widgets/KbBlockLink';
    import KbFeedback from './widgets/KbFeedback';
    import KbService from '../services/knowledge-base';
    import TermUtils from '../utils/terms';

    export default {
        components: {
            Multiselect,
            OptionList,
            SoftwareList,
            SoftwareSelect,
            TagList,
            TagSelect,
            KbBlockLink,
            KbFeedback,
        },

        data() {
            return {
                query: '',
                filtersShowing: false,
                results: [],

                categories : [], // string[] - e.g. '3D Modeling'
                termTypes  : [], // string[] - e.g. 'foundation'
                paths      : [], // string[] - e.g. 'Modeling for Film'
                software   : [], // App[] - see app service
                tags       : [], // KbTag[] - see kb service

                isLoading: true,
                isSearching: false,
            };
        },

        async mounted() {
            const getQueryArray = (name) => {
                const str = this.$route.query[name] || '';
                return str.split(',').filter(s => s.length > 0);
            };
            const getQueryIntArray = (name) => {
                return getQueryArray(name).map(s => parseInt(s));
            };

            this.isLoading = true;

            this.query = this.$route.query.q || '';

            const qTermTypes  = getQueryArray('term-types');
            const qCategories = getQueryIntArray('categories');
            const qPaths      = getQueryIntArray('paths');
            const qSoftware   = getQueryIntArray('software');
            const qTags       = getQueryIntArray('tags');

            try {
                await Promise.all([
                    this.$store.dispatch('ensureAllTermTypes'),
                    this.$store.dispatch('ensureKbCategories'),
                    this.$store.dispatch('ensureAllPaths'),
                    this.$store.dispatch('ensureAllApps'),
                    Promise
                        .all(qTags.map(KbService.getTag))
                        .then(tags => this.tags = tags),
                ]);

                this.termTypes = qTermTypes
                    .filter(t => this.termTypeOptions.includes(t));

                this.categories = qCategories
                    .map(id => this.categoryOptions.find(c => c.id === id))
                    .filter(c => c)
                    .map(c => c.label);

                this.paths = qPaths
                    .map(id => this.pathOptions.find(p => p.id === id))
                    .filter(p => p)
                    .map(p => p.name);

                this.software = qSoftware
                    .map(id => this.softwareOptions.find(s => s.id === id))
                    .filter(s => s);

                await this.loadResults();
                this.isLoading = false;

            } catch (error) {
                console.error('error loading kb search:', error);
                toastr.error(`
                    There was an error while loading the search.
                    Please try again later.
                `);
            }
        },

        methods: {
            searchTags: KbService.searchTags,

            saveQueryParams() {
                const query = {};
                if (this.query) {
                    query.q = this.query;
                }
                if (this.categories.length > 0) {
                    query.categories = this.categoriesAsIds.join(',');
                }
                if (this.termTypes.length > 0) {
                    query['term-types'] = this.termTypes.join(',');
                }
                if (this.paths.length > 0) {
                    query.paths = this.pathsAsIds.join(',');
                }
                if (this.software.length > 0) {
                    query.software = this.softwareAsIds.join(',');
                }
                if (this.tags.length > 0) {
                    query.tags = this.tagsAsIds.join(',');
                }
                this.$router.replace({ query });
            },

            async loadResults() {
                this.results = await KbService.searchArticles({
                    search:     this.query,
                    categories: this.categoriesAsIds,
                    termTypes:  this.termTypes,
                    paths:      this.pathsAsIds,
                    software:   this.softwareAsIds,
                    tags:       this.tagsAsIds,
                });

                // Preprocess each article to find search info
                const matchesQuery = strOrArray => {
                    if (Array.isArray(strOrArray)) {
                        for (const str of strOrArray) {
                            if (!matchesQuery(str)) {
                                return false;
                            }
                        }
                        return true;
                    }
                    const lcStr = strOrArray.toLowerCase();
                    const lcQuery = this.query.toLowerCase();
                    const queryParts = lcQuery.split(' ');
                    for (const part of queryParts) {
                        if (!lcStr.includes(part)) {
                            return false;
                        }
                    }
                    return !!lcQuery;
                };
                const promises = this.results.map(async article => {
                    article.search = {};

                    // Process body highlights to:
                    //  1) Insert newlines and ellipses around fragments
                    //  2) Discard excess fragments
                    if (article.bodyHighlights) {
                        article.search.highlights = article.bodyHighlights.map(hl => {
                            hl = `&hellip; ${hl} &hellip;`;
                            const frags = hl.split('\n\n').filter(frag => {
                                return !!frag.match(/<b>.*<\/b>/);
                            });
                            hl = `<p>${frags.join('</p><p>')}</p>`;
                            return hl;
                        });
                    }

                    // Process other aspects of the article to find potential
                    // search relevance
                    article.search.software = this.softwareOptions.filter(s => {
                        const inArt = article.software.includes(s.id);
                        const exact = this.softwareAsIds.includes(s.id);
                        const partial = matchesQuery(s.name);
                        return inArt && (exact || partial);
                    });
                    const articleTags = await Promise.all(
                        article.tags.map(KbService.getTag)
                    );
                    article.search.tags = articleTags.filter(t => {
                        const exact = this.tags.includes(t.id);
                        const partial = matchesQuery(t.name);
                        return exact || partial;
                    });
                    article.search.categories = this.categoryOptions.filter(c => {
                        const inArt = article.categories.includes(c.id);
                        const exact = this.categoriesAsIds.includes(c.id);
                        const partial = matchesQuery(c.label);
                        return inArt && (exact || partial);
                    }).map(c => c.label);
                    article.search.termTypes = this.termTypeOptions.filter(tt => {
                        const inArt = article.termTypes.includes(tt);
                        const exact = this.termTypes.includes(tt);
                        const partial = matchesQuery(tt);
                        return inArt && (exact || partial);
                    });
                    article.search.paths = this.pathOptions.filter(p => {
                        const inArt = article.paths.includes(p.id);
                        const exact = this.paths.includes(p.name);
                        const partial = matchesQuery(p.name);
                        return inArt && (exact || partial);
                    }).map(p => p.name);
                });
                await Promise.all(promises);
            },

            getArticleLocation(article) {
                return {
                    path: `/kb-article/${article.id}`
                };
            },

            formatSearchArticlesTitle(articles) {
                if (articles.length === 0) {
                    return 'No Articles Found';
                }
                if (articles.length === 1) {
                    return '1 Article Found';
                }
                return `${articles.length} Articles Found`;
            },

            async search() {
                this.isSearching = true;
                this.saveQueryParams();
                await this.loadResults();
                this.isSearching = false;
            },

            toggleFilters() {
                this.filtersShowing = !this.filtersShowing;
            },

            getCoverImageUrl(article) {
                if (article.coverImageUrl) {
                    return article.coverImageUrl;
                }
                if (article.categories.length > 0) {
                    const c = this.categoryOptions.find(c => {
                        return c.id === article.categories[0];
                    });
                    if (c) {
                        return c.imageUrl;
                    }
                }
                return null;
            },

            doesArticleHaveAnySearchInfo(article) {
                return (this.doesArticleHaveSearchHighlights(article) ||
                        this.doesArticleHaveSearchDetails(article));
            },

            doesArticleHaveSearchHighlights(article) {
                return (article.search.highlights &&
                        article.search.highlights.length > 0);
            },

            doesArticleHaveSearchDetails(article) {
                return [].concat(
                    article.search.software   || [],
                    article.search.tags       || [],
                    article.search.categories || [],
                    article.search.termTypes  || [],
                    article.search.paths      || [],
                ).length > 0;
            },
        },

        computed: {
            /**
             * @returns {KbCategory[]} - see kb service
             */
            categoryOptions() {
                const cats = this.$store.getters.kbCategories.slice();
                return cats.sort((a, b) => a.label.localeCompare(b.label));
            },

            /**
             * @returns {string[]}
             */
            termTypeOptions() {
                const types = this.$store.getters.allTermTypes;
                return TermUtils.sortTypes(types);
            },

            /**
             * @returns {Path[]} - see path service
             */
            pathOptions() {
                return this.$store.getters.allPaths;
            },

            /**
             * @returns {App[]} - see app service
             */
            softwareOptions() {
                const apps = this.$store.getters.allApps.slice();
                return apps.sort((a, b) => a.name.localeCompare(b.name));
            },

            categoriesAsIds() {
                return this.categories.map(label => {
                    return this.categoryOptions.find(c => c.label === label).id;
                });
            },

            pathsAsIds() {
                return this.paths.map(name => {
                    return this.pathOptions.find(p => p.name === name).id;
                });
            },

            softwareAsIds() {
                return this.software.map(s => s.id);
            },

            tagsAsIds() {
                return this.tags.map(t => t.id);
            },

            categoryOptionsAsLabels() {
                return this.categoryOptions.map(c => c.label);
            },

            pathOptionsAsNames() {
                return this.pathOptions.map(p => p.name);
            },
        },

        watch: {
            categories: function() {
                if (!this.isLoading) {
                    this.search();
                }
            },
            termTypes: function() {
                if (!this.isLoading) {
                    this.search();
                }
            },
            paths: function() {
                if (!this.isLoading) {
                    this.search();
                }
            },
            software: function() {
                if (!this.isLoading) {
                    this.search();
                }
            },
            tags: function() {
                if (!this.isLoading) {
                    this.search();
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .clear-button {
        border: none;
        background: none;
    }

    .link-button {
        border: none;
        background: none;
        color: #33ccff;
        &:hover {
            text-decoration: underline;
        }
    }

    .loading-msg {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100px;
        width: 100%;
        font-style: italic;
    }

    .form-group {
        margin-bottom: 20px;

        label {
            margin-bottom: 10px;
        }
    }

    .h1-panel {
        display: flex;

        .panel-heading,
        .panel-body {
            height: auto;
            width: auto;
            display: flex;
            align-items: center;
        }

        h1 {
            margin: 10px 0;

            a {
                color: inherit;
                text-decoration: none;
                &:hover {
                    color: #33ccff;
                }
            }
        }

        h2 {
            margin: 10px 15px;
            font-size: 20px;
        }
    }

    .search-panel {
        .panel-body {
            padding: 40px 20px;
        }

        h2 {
            text-align: center;
            margin: 0 0 20px 0;
        }

        .form-group {
            width: 80%;
            max-width: 600px;
            display: block;
            margin: 0 auto;
            position: relative;
            font-size: 16px;
            input {
                padding-right: calc(0.5rem + 45px) !important;
                font-size: inherit;
            }
            button {
                position: absolute;
                width: 45px;
                height: 45px;
                top: 0;
                right: 0;
                color: #b0b0b0;
                text-decoration: none;
                &:hover {
                    background-color: #363636;
                }
            }
        }

        .link-button {
            display: block;
            margin: 30px auto 0;
        }
    }

    .filter-panel {
        .panel-body {
            padding: 40px 20px;
        }

        h2 {
            margin: 0 0 40px 0;
        }
    }

    .results-panel {
        .panel-body {
            padding: 40px 20px;
        }

        h2 {
            margin: 0 0 40px 0;
        }

        .no-results {
            h2 {
                margin: 0;
            }
        }

        ol {
            list-style-type: none;
            margin: 0 0 -20px 0;
            padding: 0;
        }

        li {
            margin-bottom: 20px;
        }

        .info-group {
            color: #b0b0b0;
            display: inline-block;
            font-size: 14px;
            vertical-align: top;
        }

        .option-list {
        }

        .article-block {
            position: relative;
            z-index: 0;
            min-height: 128px;
        }

        .article-overview {
            min-height: 128px;
            padding: 15px 20px 15px 148px;

            img {
                position: absolute;
                top: 0;
                left: 0;
                width: 128px;
                height: 128px;
                object-fit: cover;
            }

            h3 {
                margin: 10px 0;
                color: #33ccff;
            }
        }

        .article-search-info {
            border-top: 1px solid #454545;
            background-color: #303030;
            height: 100%;
        }

        .article-quotes {
            margin: 20px 0 0;
            padding: 0 15px;
        }

        .article-details {
            margin: 20px 0;
            padding: 0 15px;
        }
    }
</style>

<style lang="scss">
    .kb-search {
        .term-type-multiselect button {
            text-transform: capitalize;
        }

        .software-select {
            margin-bottom: 30px;

            .software-list {
                margin-top: 20px;

                > li {
                    width: 100px;
                    .delete-btn {
                        right: 5px;
                    }
                }
            }
        }

        .results-panel {
            .option-list {
                margin-top: 10px;
                margin-bottom: 1px;

                .option {
                    margin-bottom: 4px;
                }
                .option-inner {
                    background-color: rgba(0, 0, 0, 0.2);
                }
            }
        }
    }
</style>



// WEBPACK FOOTER //
// KbSearch.vue?7107e0ee